import type { TransferReceiptModel, TransferReceiptOutcome, TransferReceiptRow } from "./transferReceipt";

export interface ReceiptTextStyle {
  font: "sans" | "mono";
  bold: boolean;
  size: number;
  color: string;
  align: "left" | "center" | "right";
}

/**
 * One surface the receipt is drawn on, in the layout's own units (a 1080-wide page). The canvas draws the
 * shared image and jsPDF the vector PDF; both run `paintTransferReceipt`, so the two cannot drift apart.
 */
export interface ReceiptPainter {
  fillRect(x: number, y: number, width: number, height: number, color: string): void;
  fillRoundedRect(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    color: string,
    topOnly?: boolean,
  ): void;
  fillCircle(x: number, y: number, radius: number, color: string): void;
  line(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: string,
    width: number,
    dash?: [number, number],
  ): void;
  /** `y` is the vertical middle of the line of text. */
  text(value: string, x: number, y: number, style: ReceiptTextStyle): void;
  measure(value: string, style: ReceiptTextStyle): number;
  logo(x: number, y: number, width: number, height: number): void;
}

export const RECEIPT_WIDTH = 1080;
const MARGIN = 48;
const TICKET_X = MARGIN;
const TICKET_WIDTH = RECEIPT_WIDTH - MARGIN * 2;
const INNER_X = TICKET_X + 64;
const INNER_WIDTH = TICKET_WIDTH - 128;
const RADIUS = 40;
const HEADER_HEIGHT = 196;
const SUMMARY_HEIGHT = 410;
const NOTICE_LINE = 38;
const ROW_HEIGHT = 84;
const BLOCK_ROW_HEIGHT = 120;
const SECTION_TITLE_HEIGHT = 72;
const SECTION_GAP = 28;
const FOOTER_HEIGHT = 150;
const NOTCH_RADIUS = 26;
const LOGO_WIDTH = 380;

export const RECEIPT_COLORS = {
  page: "#E9EFEC",
  ticket: "#FFFFFF",
  brand: "#00735C",
  ink: "#1C1917",
  muted: "#78716C",
  rule: "#D6D3D1",
  hairline: "#EFEDEC",
};

const PILL: Record<TransferReceiptOutcome, { fill: string; ink: string }> = {
  settled: { fill: "#E3F2EC", ink: RECEIPT_COLORS.brand },
  pending: { fill: "#FBF1DA", ink: "#7A5A18" },
  rejected: { fill: "#FCE7E3", ink: "#8A2418" },
};

const style = (overrides: Partial<ReceiptTextStyle>): ReceiptTextStyle => ({
  font: "sans",
  bold: false,
  size: 28,
  color: RECEIPT_COLORS.ink,
  align: "left",
  ...overrides,
});

function wrap(
  painter: ReceiptPainter,
  value: string,
  maxWidth: number,
  textStyle: ReceiptTextStyle,
): string[] {
  const lines: string[] = [];
  let current = "";
  for (const word of value.split(/\s+/)) {
    const next = current ? `${current} ${word}` : word;
    if (current && painter.measure(next, textStyle) > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/** The largest size that fits, else the smallest one cut with an ellipsis. */
function fit(
  painter: ReceiptPainter,
  value: string,
  maxWidth: number,
  base: ReceiptTextStyle,
  sizes: number[],
): { value: string; style: ReceiptTextStyle } {
  for (const size of sizes) {
    const candidate = { ...base, size };
    if (painter.measure(value, candidate) <= maxWidth) return { value, style: candidate };
  }
  const smallest = { ...base, size: sizes[sizes.length - 1] };
  let cut = value;
  while (cut.length > 1 && painter.measure(`${cut}…`, smallest) > maxWidth) cut = cut.slice(0, -1);
  return { value: `${cut}…`, style: smallest };
}

const NOTICE_STYLE = style({ size: 26, align: "center" });

function noticeLines(painter: ReceiptPainter, model: TransferReceiptModel): string[] {
  return model.notice ? wrap(painter, model.notice, INNER_WIDTH, NOTICE_STYLE) : [];
}

const rowHeight = (row: TransferReceiptRow) => (row.block ? BLOCK_ROW_HEIGHT : ROW_HEIGHT);

const sectionHeight = (rows: TransferReceiptRow[], titled: boolean) =>
  (titled ? SECTION_TITLE_HEIGHT : 0) + rows.reduce((total, row) => total + rowHeight(row), 0);

export function receiptHeight(painter: ReceiptPainter, model: TransferReceiptModel): number {
  const notice = noticeLines(painter, model).length;
  const rows =
    model.sections.reduce((total, section) => total + sectionHeight(section.rows, !!section.title), 0) +
    SECTION_GAP * Math.max(0, model.sections.length - 1);
  const summary = SUMMARY_HEIGHT + (notice ? notice * NOTICE_LINE + 20 : 0);
  return HEADER_HEIGHT + summary + 56 + rows + FOOTER_HEIGHT + MARGIN * 2;
}

function drawRow(painter: ReceiptPainter, row: TransferReceiptRow, top: number): void {
  const label = style({ color: RECEIPT_COLORS.muted });
  const valueStyle = style({
    font: row.mono ? "mono" : "sans",
    bold: true,
    align: row.block ? "left" : "right",
    color: row.highlight ? RECEIPT_COLORS.brand : RECEIPT_COLORS.ink,
  });
  if (row.block) {
    painter.text(row.label, INNER_X, top + 34, label);
    const value = fit(painter, row.value, INNER_WIDTH, valueStyle, [30, 26, 22]);
    painter.text(value.value, INNER_X, top + 82, value.style);
    return;
  }
  const y = top + ROW_HEIGHT / 2;
  painter.text(row.label, INNER_X, y, label);
  const labelWidth = painter.measure(row.label, label);
  const value = fit(
    painter,
    row.value,
    INNER_WIDTH - labelWidth - 40,
    valueStyle,
    row.mono ? [26, 22, 18] : [30, 26, 22],
  );
  painter.text(value.value, INNER_X + INNER_WIDTH, y, value.style);
}

export function paintTransferReceipt(
  painter: ReceiptPainter,
  model: TransferReceiptModel,
  logoAspect: number | null,
): void {
  const height = receiptHeight(painter, model);
  const ticketHeight = height - MARGIN * 2;
  const top = MARGIN;
  const center = RECEIPT_WIDTH / 2;
  const notice = noticeLines(painter, model);
  const summaryHeight = SUMMARY_HEIGHT + (notice.length ? notice.length * NOTICE_LINE + 20 : 0);

  painter.fillRect(0, 0, RECEIPT_WIDTH, height, RECEIPT_COLORS.page);
  painter.fillRoundedRect(TICKET_X, top, TICKET_WIDTH, ticketHeight, RADIUS, RECEIPT_COLORS.ticket);
  painter.fillRoundedRect(TICKET_X, top, TICKET_WIDTH, HEADER_HEIGHT, RADIUS, RECEIPT_COLORS.brand, true);
  if (logoAspect) {
    const logoHeight = LOGO_WIDTH / logoAspect;
    painter.logo(center - LOGO_WIDTH / 2, top + (HEADER_HEIGHT - logoHeight) / 2, LOGO_WIDTH, logoHeight);
  }

  let y = top + HEADER_HEIGHT + 70;
  painter.text(
    model.title.toUpperCase(),
    center,
    y,
    style({ bold: true, size: 30, color: RECEIPT_COLORS.muted, align: "center" }),
  );
  y += 48;
  painter.text(model.date, center, y, style({ size: 26, color: RECEIPT_COLORS.muted, align: "center" }));

  y += 76;
  const pill = PILL[model.outcome];
  const pillStyle = style({ bold: true, size: 28, color: pill.ink });
  const pillWidth = painter.measure(model.status, pillStyle) + 96;
  const pillX = center - pillWidth / 2;
  painter.fillRoundedRect(pillX, y - 28, pillWidth, 56, 28, pill.fill);
  painter.fillCircle(pillX + 40, y, 9, pill.ink);
  painter.text(model.status, pillX + 62, y + 1, pillStyle);

  for (const line of notice) {
    y += NOTICE_LINE;
    painter.text(line, center, y + 14, { ...NOTICE_STYLE, color: pill.ink });
  }
  if (notice.length) y += 20;

  y += 84;
  painter.text(model.amountLabel, center, y, style({ color: RECEIPT_COLORS.muted, align: "center" }));

  y += 82;
  const amountStyle = style({ bold: true, size: 88 });
  const currencyStyle = style({ bold: true, size: 34, color: RECEIPT_COLORS.muted });
  const amountWidth = painter.measure(model.amount, amountStyle);
  const currencyWidth = painter.measure(model.currency, currencyStyle);
  const amountX = center - (amountWidth + currencyWidth + 18) / 2;
  painter.text(model.amount, amountX, y, amountStyle);
  painter.text(model.currency, amountX + amountWidth + 18, y + 14, currencyStyle);

  // The tear line of a paper voucher: notches on both edges joined by a dashed rule.
  const perforation = top + HEADER_HEIGHT + summaryHeight + 28;
  for (const x of [TICKET_X, TICKET_X + TICKET_WIDTH]) {
    painter.fillCircle(x, perforation, NOTCH_RADIUS, RECEIPT_COLORS.page);
  }
  painter.line(
    TICKET_X + NOTCH_RADIUS + 16,
    perforation,
    TICKET_X + TICKET_WIDTH - NOTCH_RADIUS - 16,
    perforation,
    RECEIPT_COLORS.rule,
    3,
    [14, 12],
  );

  y = perforation + 28;
  model.sections.forEach((section, sectionIndex) => {
    if (sectionIndex > 0) {
      y += SECTION_GAP / 2;
      painter.line(TICKET_X, y, TICKET_X + TICKET_WIDTH, y, RECEIPT_COLORS.rule, 2);
      y += SECTION_GAP / 2;
    }
    if (section.title) {
      painter.text(
        section.title.toUpperCase(),
        INNER_X,
        y + SECTION_TITLE_HEIGHT / 2 + 6,
        style({ bold: true, size: 24, color: RECEIPT_COLORS.muted }),
      );
      y += SECTION_TITLE_HEIGHT;
    }
    section.rows.forEach((row, index) => {
      const height = rowHeight(row);
      drawRow(painter, row, y);
      if (index < section.rows.length - 1) {
        painter.line(INNER_X, y + height, INNER_X + INNER_WIDTH, y + height, RECEIPT_COLORS.hairline, 2);
      }
      y += height;
    });
  });

  painter.text(
    model.footer,
    center,
    top + ticketHeight - FOOTER_HEIGHT / 2,
    style({ size: 26, color: RECEIPT_COLORS.muted, align: "center" }),
  );
}
